import React from "react";
import { format } from "date-fns";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";
import { makeStyles } from "@material-ui/styles";
import { Paper, Typography } from "@material-ui/core";

import { useDeepEffect, useDeepMemo, useQuery, useSafeState, useSetting, useTime, useUnit } from "../../../hooks";
import SettingKeys from "../../../utils/SettingKeys";
import MuiTable from "../../../components/MuiTable";
import { getFpoList, getSortedSizeKeyList } from "../../../helpers/orderHelper";
import Table from "../../../components/Table";
import { getDistinctValues, isEmpty } from "../../../utils/helpers";

const NON_SB_FIELDS = ["id", "orderQty", "label", "product", "color", "flowRef", "customerFlowRef", "inseam", "destination", "delivery", "total"];

const useStyles = makeStyles({
  table: {
    "& th, & td": {
      border: "1px solid rgba(224, 224, 224, 1)"
    }
  },
  rowCell: {
    padding: 0,
    fontSize: 12,
    "&:last-child": {
      padding: 0,
      fontSize: 12
    }
  },
  footer: {
    display: "flex",
    flex: 1,
    padding: "1em",
    justifyContent: "space-between"
  }
});

const FpoDetails = props => {
  const { userActivity, translate } = props;
  const classes = useStyles();
  const { utc_offset } = useTime();
  const [selectedKey, setSelectedKey] = useSafeState();

  const fpo = JSON.parse(userActivity?.currValue || "{}");

  const flowInfoFields = useSetting(SettingKeys.ENTITY_FIELDS, "flowInfo");
  const fields = ["poRef", ...flowInfoFields, "fpo", "fpoQty", "unit"];

  const { data: order = {} } = useQuery(fpo?.orderId && { url: `orders/${fpo?.orderId}`, params: { view: "deep" } });

  const { allUnitList } = useUnit(order);

  const columns = useDeepMemo(() => {
    let cols = [
      { dataKey: "fpo", title: "FPO", length: 100, render: (value, shortValue) => shortValue || "-" },
      { dataKey: "poRef", title: "PO Ref" },
      { dataKey: "unit", title: "Unit", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "productName", title: "Product" },
      { dataKey: "styleNo", title: "Style No" },
      { dataKey: "style", title: "Style" },
      { dataKey: "color", title: "Color" },
      { dataKey: "inseam", title: "Inseam", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "destination", title: "Destination", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "deliveryDate", title: "Delivery Date", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "customerFlowRef", title: "BPO", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "flowRef", title: "Line ID", render: (value, shortValue) => shortValue || "-" },
      { dataKey: "fpoQty", title: "FPO Qty" },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "extra", title: "Extra(%)" },
      { dataKey: "status", title: "Status" }
    ];

    return cols.filter(c => fields.includes(c.dataKey));
  }, [fields]);

  const rows = useDeepMemo(() => {
    const fpoList = getFpoList(fpo);
    return fpoList.flatMap(row => {
      let children = [];
      if (row.flowInfoList.length > 1) {
        children = row.flowInfoList
          .map(fi => ({
            ...fi,
            colorId: `${fi.product?.id}-${fi.style}-${fi.color}`,
            key: fi.id,
            parentKey: row.fpo,
            poRef: fpo.poRef,
            productName: fi.product?.name,
            unit: allUnitList?.find(u => u.unitId === fi.primaryUnitId)?.name
          }))
          .sort((a, b) => (a.colorId !== b.colorId ? (a.colorId < b.colorId ? -1 : 1) : a.serialNo - b.serialNo));
      }
      return [
        {
          ...row,
          poRef: fpo.poRef,
          unit: allUnitList?.find(u => u.unitId === row.unitId)?.name
        },
        ...children
      ];
    });
  }, [fpo, allUnitList]);

  useDeepEffect(() => {
    if (isEmpty(rows)) return;
    if (rows.some(r => !isEmpty(r.parentKey))) {
      setSelectedKey(rows.find(r => !isEmpty(r.parentKey) && r.parentKey));
    } else {
      setSelectedKey(rows[0].key);
    }
  }, [rows]);

  const selectedRow = rows.find(r => (!isEmpty(r.parentKey) && r.parentKey === selectedKey) || r.key);

  //** Prepare Size Breakup Columns for View mode **/
  const { columns: sbColumns, headerRows } = useDeepMemo(() => {
    if (isEmpty(fpo?.flowInfoList)) return [];

    let flowInfoList = selectedRow?.flowInfoList || [];
    let sizeBreakupList = [];
    let hasInseam = flowInfoList.some(c => !isEmpty(c.inseam));

    sizeBreakupList = flowInfoList
      .flatMap(fi => fi.fpoSizeBreakupList || [])
      .map(sb => (hasInseam ? { ...sb, sizeGroup: "G1", sizeKey: `G1_${sb.size}` } : sb));

    const sbMap = sizeBreakupList.reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb }), {});
    const sbList = Object.values(sbMap).sort((a, b) => a.serialNo - b.serialNo);
    let columns = [];
    if (flowInfoList?.length > 1) {
      let cols = [
        { dataKey: "product", title: "Product", align: "center" },
        { dataKey: "color", title: "Color", align: "center" },
        { dataKey: "flowRef", title: "Flow Ref", align: "center" },
        { dataKey: "customerFlowRef", title: "Customer Flow Ref", align: "center" },
        { dataKey: "inseam", title: "Inseam", align: "center" },
        { dataKey: "destination", title: "Destination", align: "center" },
        { dataKey: "delivery", title: "Delivery Date", align: "center" }
      ];
      const productCount = getDistinctValues(flowInfoList.map(c => c.product?.id))?.length;
      const colorCount = getDistinctValues(flowInfoList.map(c => c.color))?.length;
      const flowRefCount = getDistinctValues(flowInfoList.map(c => c.flowRef))?.length;
      const cFlowRefCount = getDistinctValues(flowInfoList.map(c => c.customerFlowRef))?.length;
      const inseamCount = getDistinctValues(flowInfoList.map(c => c.inseam))?.length;
      const destCount = getDistinctValues(flowInfoList.map(c => c.destination))?.length;
      const deliveryCount = getDistinctValues(flowInfoList.map(c => c.delivery))?.length;

      if (productCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "product");
      }
      if (colorCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "color");
      }
      if (flowRefCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "flowRef");
      }
      if (cFlowRefCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "customerFlowRef");
      }
      if (inseamCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "inseam");
      }
      if (destCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "destination");
      }
      if (deliveryCount <= 1) {
        cols = cols.filter(c => c.dataKey !== "delivery");
      }
      columns = [...cols];
    }

    const sizeKeys = getSortedSizeKeyList(sbList);
    let groupRow = {};
    let sizeList = [];
    let sizeGroup;
    for (let i = 0; i < sizeKeys.length; i++) {
      const sizeKey = sizeKeys[i];
      const sb = sbList.find(sb => sb.sizeKey === sizeKey);
      let leftBorder = false;
      if (sizeGroup !== sb.sizeGroup) {
        leftBorder = true;
        sizeGroup = sb.sizeGroup;
        groupRow[sizeKey] = sb.sizeGroup;
      }
      sizeList.push({
        dataKey: sizeKey,
        title: sizeKey.split("_")[1],
        element: ({ record, column }) => <span>{record[column.dataKey] || "-"}</span>,
        leftBorder
      });
    }

    const haveSizeGroup = !hasInseam && getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)))?.length > 0;

    columns = [...columns, ...sizeList, { dataKey: "total", title: "Total", leftBorder: true }];
    columns = columns.map(c => ({
      ...c,
      align: "center",
      colSpan: ({ column, row, rowIdx }) =>
        !haveSizeGroup || rowIdx === 1 || NON_SB_FIELDS.includes(c.dataKey)
          ? 1
          : isEmpty(row[column.dataKey])
          ? 0
          : getDistinctValues(sbList.filter(sb => sb.sizeGroup === row[column.dataKey]).map(sb => sb.sizeKey))?.length
    }));

    let headerRows = [];
    if (!hasInseam && Object.keys(groupRow)?.length > 0) {
      headerRows = [
        groupRow,
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [c.dataKey]: translate(`resources.orders.fields.flowInfo.${c.dataKey}`, {
              _: c.title
            })
          }),
          {}
        )
      ];
    } else {
      headerRows = [
        columns.reduce(
          (acc, c) => ({
            ...acc,
            [c.dataKey]: translate(`resources.orders.fields.flowInfo.${c.dataKey}`, {
              _: c.title
            })
          }),
          {}
        )
      ];
    }

    return { columns, headerRows };
  }, [selectedRow, fpo]);

  //** Prepare Size Breakup Rows for View mode **/
  const sbRows = useDeepMemo(() => {
    if (isEmpty(fpo?.flowInfoList)) return [];

    let flowInfoList = selectedRow?.flowInfoList || [];
    flowInfoList = flowInfoList
      .map(fi => ({ ...fi, colorId: `${fi.product?.id}-${fi.style}-${fi.color}`, sbList: fi.fpoSizeBreakupList }))
      .sort((a, b) => (a.colorId !== b.colorId ? (a.colorId < b.colorId ? -1 : 1) : a.serialNo - b.serialNo));

    const hasInseam = flowInfoList.some(c => !isEmpty(c.inseam));

    let rows = flowInfoList.map(fi => ({
      id: fi.id,
      product: fi.product?.name,
      color: fi.color,
      flowRef: fi.flowRef,
      customerFlowRef: fi.customerFlowRef,
      inseam: fi.inseam,
      destination: fi.destination,
      delivery: !isEmpty(utc_offset) && !isEmpty(fi.exFactoryDate) ? `${format(fi.exFactoryDate, utc_offset, "dd MMM, yy")}` : "",
      total: fi.sbList?.reduce((acc, sb) => acc + sb.qty, 0),
      orderQty: fi.orderQty,
      ...fi.sbList?.map(sb => (hasInseam ? { ...sb, sizeKey: `G1_${sb.size}` } : sb)).reduce((acc, sb) => ({ ...acc, [sb.sizeKey]: sb.qty }), {})
    }));
    if (rows.every(row => row.total === 0)) {
      rows = [];
    }
    if (rows?.length > 1 && !isEmpty(flowInfoList)) {
      const totalRow = columns
        .filter(c => c.dataKey === "total" || !NON_SB_FIELDS.includes(c.dataKey))
        .reduce((acc, c) => ({ ...acc, [c.dataKey]: rows.reduce((acc, row) => acc + (row[c.dataKey] || 0), 0) }), {});
      const cols = columns.filter(c => c.dataKey !== "total" && NON_SB_FIELDS.includes(c.dataKey));
      if (!isEmpty(cols)) {
        rows.push({ ...totalRow, [cols[cols.length - 1].dataKey]: "Total", isTotalRow: true });
      }
    }
    return rows;
  }, [selectedRow, fpo, sbColumns]);

  return (
    <PerfectScrollbar>
      <div style={{ padding: 16 }}>
        <Typography style={{ marginBottom: 12, color: "#666666" }} variant="h6">
          Factory Production Order
        </Typography>
        <Paper>
          <MuiTable columns={columns} rows={rows} idKey="key" parentIdKey="parentKey" defaultExpanded />
        </Paper>
      </div>

      <div style={{ padding: 16 }}>
        <Typography style={{ marginBottom: 12, color: "#666666" }} variant="h6">
          Size Breakup
        </Typography>
        <Paper>
          <Table
            classes={{ table: classes.nowrapTable }}
            columns={sbColumns}
            headerRows={headerRows}
            rows={sbRows}
            i18nKey="resources.orders.fields.flowInfo"
            headerCellStyle={column => ({
              width: NON_SB_FIELDS.includes(column?.dataKey) ? 150 : undefined,
              borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined,
              fontWeight: "bold",
              backgroundColor: "#C6D0DA"
            })}
            cellStyle={({ column, record }) => ({
              fontWeight: record.isTotalRow ? "bold" : "normal",
              borderLeft: column.leftBorder ? "1px solid rgba(224, 224, 224, 1)" : undefined
            })}
          />
        </Paper>
      </div>
    </PerfectScrollbar>
  );
};

export default FpoDetails;
